// ─────────────────────────────────────────────────────────────────
// Glass-i — Off-cut register PIVOT view.
//
// Off-cut piece sizes are ad-hoc, so this pivot does NOT spread sizes across
// columns. It summarises each glass item into three standard measures —
// Pieces / SQM / SQFT — and the operator narrows the set with CATEGORY /
// BRAND / THICKNESS / DATE filters. Every figure comes from the server
// aggregate (GET /api/erp/inventory/offcuts/pivot) — DB values only. Rows are
// server-paginated; a summary row carries the grand totals over ALL matching
// off-cuts (not just the visible page).
//
// Area (SQM/SQFT) shows at exactly 3 dp per CLAUDE.md rule 5A; Pieces stays a
// whole count. Category + brand option lists come from the same response.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Table, Select, InputNumber, DatePicker, Button, Space, Tag, Tooltip, Typography, message
} from 'antd';
import { TableOutlined, ClearOutlined } from '@ant-design/icons';

import offcutApi from './offcutApi';
import { formatArea, formatThickness } from './offcutHelpers';
import './OffcutsPage.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const NF0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const PAGE_SIZE_OPTIONS = ['25', '50', '100'];
const DATE_FMT = 'YYYY-MM-DD';

function fmtPieces(v) {
  const n = Number(v);
  return Number.isFinite(n) ? NF0.format(Math.round(n)) : '—';
}
function errMessage(e, fallback) {
  return (e && e.response && e.response.data && e.response.data.error && e.response.data.error.message)
    || (e && e.message) || fallback;
}

export default function OffcutsPivotView({ filters }) {
  // Pivot-only filters (independent of the page's search box).
  const [category, setCategory]   = useState(undefined);
  const [brand, setBrand]         = useState(undefined);
  const [thickness, setThickness] = useState(null);
  const [dateRange, setDateRange] = useState(null);   // [dayjs, dayjs] | null

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  // Monotonic request id — changing a filter while on page>1 schedules both a
  // page-reset and a fetch for the old page; only the latest request may write
  // state, so a slow stale response can never overwrite the current one.
  const reqIdRef = useRef(0);

  const search = (filters && filters.search) || undefined;

  // Any filter change resets to page 1.
  const filterKey = useMemo(
    () => JSON.stringify({
      search, category, brand,
      thickness: thickness == null ? null : thickness,
      from: dateRange && dateRange[0] ? dateRange[0].format(DATE_FMT) : null,
      to:   dateRange && dateRange[1] ? dateRange[1].format(DATE_FMT) : null
    }),
    [search, category, brand, thickness, dateRange]
  );
  useEffect(() => { setPage(1); }, [filterKey]);

  const load = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const params = { limit: pageSize, offset: (page - 1) * pageSize };
      if (search) params.search = search;
      if (category) params.category = category;
      if (brand) params.brand = brand;
      if (thickness != null && thickness !== '') params.thickness = thickness;
      if (dateRange && dateRange[0]) params.dateFrom = dateRange[0].format(DATE_FMT);
      if (dateRange && dateRange[1]) params.dateTo = dateRange[1].format(DATE_FMT);

      const out = await offcutApi.pivot(params);
      if (myReq !== reqIdRef.current) return;   // a newer request superseded this one
      setData(out || null);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      message.error({ content: errMessage(e, 'Failed to load the off-cut pivot.'), key: 'ocpv-load' });
      setData(null);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const rows       = (data && data.rows) || [];
  const grandTotal = (data && data.grandTotal) || { qty: 0, sqmt: 0, sqft: 0 };
  const pageInfo   = (data && data.page) || {};
  const rowTotalCount = Number(pageInfo.rowTotalCount || 0);
  const categoryOptions = (data && data.categoryOptions) || [];
  const brandOptions    = (data && data.brandOptions) || [];

  const hasFilters = !!(category || brand || (thickness != null && thickness !== '') || dateRange);
  const clearFilters = useCallback(() => {
    setCategory(undefined); setBrand(undefined); setThickness(null); setDateRange(null);
  }, []);

  const columns = useMemo(() => ([
    {
      title: 'Glass item', dataIndex: 'itemDesc', key: 'itemDesc', ellipsis: true,
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <Tooltip title={v}><span style={{ fontWeight: 600 }}>{v}</span></Tooltip>
          {r.brand ? <span style={{ fontSize: 10.5, color: '#64748b' }}>{r.brand}</span> : null}
        </div>
      )
    },
    {
      title: 'Category', dataIndex: 'glassCategory', key: 'glassCategory', width: 130,
      render: (v) => (v ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>{v}</Tag> : <Text type="secondary">—</Text>)
    },
    {
      title: 'Thk', dataIndex: 'thicknessMm', key: 'thicknessMm', width: 70, align: 'right',
      render: (v) => (v != null ? `${formatThickness(v)}mm` : <Text type="secondary">—</Text>)
    },
    {
      title: 'Pieces', dataIndex: 'pieces', key: 'pieces', width: 96, align: 'right',
      render: (v) => <span className="tabular-nums" style={{ fontWeight: 600 }}>{fmtPieces(v)}</span>
    },
    {
      title: 'SQM (m²)', dataIndex: 'sqmt', key: 'sqmt', width: 110, align: 'right',
      render: (v) => <span className="tabular-nums">{formatArea(v)}</span>
    },
    {
      title: 'SQFT (ft²)', dataIndex: 'sqft', key: 'sqft', width: 110, align: 'right',
      render: (v) => <span className="tabular-nums">{formatArea(v)}</span>
    }
  ]), []);

  return (
    <Card
      className="offcut-grid offcut-pivot"
      bordered={false}
      bodyStyle={{ padding: 0 }}
      title={(
        <span className="offcut-pivot__title">
          <span className="offcut-pivot__ic"><TableOutlined /></span>
          Off-cut pivot
          <span className="offcut-pivot__sub">Pieces / SQM / SQFT per glass item</span>
        </span>
      )}
    >
      {/* Filter bar */}
      <div className="offcut-pivot__filters">
        <Select
          allowClear showSearch size="small" placeholder="All categories"
          value={category} onChange={(v) => setCategory(v || undefined)}
          options={categoryOptions.map((c) => ({ value: c, label: c }))}
          optionFilterProp="label" style={{ width: 170 }}
        />
        <Select
          allowClear showSearch size="small" placeholder="All brands"
          value={brand} onChange={(v) => setBrand(v || undefined)}
          options={brandOptions.map((b) => ({ value: b, label: b }))}
          optionFilterProp="label" style={{ width: 150 }}
          notFoundContent={brandOptions.length === 0
            ? <span style={{ fontSize: 11.5, color: '#94a3b8' }}>No brands recorded</span> : null}
        />
        <InputNumber
          size="small" placeholder="Thickness (mm)" value={thickness}
          onChange={(v) => setThickness(v == null ? null : v)}
          min={0} step={0.5} style={{ width: 140 }}
        />
        <RangePicker
          size="small" value={dateRange} onChange={(v) => setDateRange(v)}
          allowEmpty={[true, true]} style={{ width: 240 }} format="DD-MMM-YYYY"
        />
        <Tooltip title="Clear pivot filters">
          <Button size="small" icon={<ClearOutlined />} onClick={clearFilters} disabled={!hasFilters} />
        </Tooltip>
      </div>

      <Table
        rowKey="itemKey"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={rows}
        sticky
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page, pageSize, total: rowTotalCount, showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS, size: 'small',
          onChange: (p, ps) => { if (ps !== pageSize) { setPageSize(ps); setPage(1); } else { setPage(p); } },
          showTotal: (t) => (t === 0 ? 'No items' : `${t} glass item${t === 1 ? '' : 's'}`)
        }}
        locale={{ emptyText: loading ? ' ' : 'No off-cuts match these filters.' }}
        summary={() => (rows.length ? (
          <Table.Summary fixed>
            <Table.Summary.Row className="offcut-pivot__total">
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>All items{hasFilters ? ' (filtered)' : ''}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align="right">
                <strong className="tabular-nums">{fmtPieces(grandTotal.qty)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} align="right">
                <strong className="tabular-nums">{formatArea(grandTotal.sqmt)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <strong className="tabular-nums">{formatArea(grandTotal.sqft)}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        ) : null)}
      />
    </Card>
  );
}
